import type { DesktopUpdateState } from "@otrack/domain";
import { Icon } from "@otrack/ui";
import { useApp } from "./context";

/** What an update needs from the User right now, or null when there's nothing to do. */
export function updateAction(state: DesktopUpdateState): string | null {
  switch (state.phase) {
    case "available": return `Update to ${state.update.version}`;
    case "downloading": return `Downloading ${Math.floor((state.receivedBytes / Math.max(1, state.totalBytes)) * 100)}%`;
    case "installing": return "Installing update";
    case "installed": return "Restart to update";
    case "ready": return "Finish the update";
    case "error": return state.update ? "Retry update" : null;
    default: return null;
  }
}

/** How the update stands, in a sentence for Settings. */
export function updateStatus(state: DesktopUpdateState): string {
  switch (state.phase) {
    case "idle": return "Checks for updates while you're signed in.";
    case "checking": return "Checking for updates…";
    case "current": return "You're on the latest version.";
    case "available": return `Version ${state.update.version} is ready to download.`;
    case "downloading": return `Downloading version ${state.update.version}…`;
    case "installing": return `Installing version ${state.update.version}…`;
    case "installed": return `Version ${state.update.version} is installed. Restart ontrack to use it.`;
    case "ready": return `The installer for ${state.update.version} is open. Follow it to finish, then reopen ontrack.`;
    case "error": return state.message;
  }
}

/** A quiet pill in the Island, only once a newer version exists; one click moves it along. */
export function UpdatePill() {
  const desktop = useApp().services.desktop;
  if (!desktop) return null;
  const label = updateAction(desktop.updates);
  if (!label) return null;
  const busy = desktop.updates.phase === "downloading" || desktop.updates.phase === "installing";
  return <button type="button" className="ot-update-pill" onClick={desktop.installUpdate} disabled={busy} aria-busy={busy}>
    <Icon name={desktop.updates.phase === "installed" ? "refresh" : "download"}/>{label}
  </button>;
}
